<template>
  <div 
    :data-dropdown-container="`order-${order.id}`" 
    class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 relative"
  >
    <!-- Three Dots Menu -->
    <div
      class="absolute top-3 right-3"
      @click.stop
    >
      <DropdownMenu
        :id="`order-${order.id}`"
        button-label="Order actions"
        width="md"
      >
        <!-- View Details -->
        <DropdownMenuItem
          :icon="EyeIcon"
          variant="info"
          @click="$emit('view', order)"
        >
          {{ $t('app.views.orders.buttons.view') }}
        </DropdownMenuItem>
        
        <!-- Edit Order -->
        <DropdownMenuItem
          v-if="order.status !== 'completed' && order.status !== 'cancelled' && !order.is_paid"
          :icon="PencilIcon"
          variant="default"
          @click="$emit('edit', order)"
        >
          {{ $t('app.actions.edit') }}
        </DropdownMenuItem>
        
        <!-- Status Actions -->
        <DropdownMenuDivider v-if="order.status === 'ready' || canCancel" />
        
        <DropdownMenuItem
          v-if="order.status === 'ready'"
          :icon="CheckCircleIcon"
          variant="success"
          @click="$emit('complete', order.id)"
        >
          {{ $t('app.views.orders.buttons.complete') }}
        </DropdownMenuItem>
        
        <DropdownMenuDivider v-if="order.status === 'ready' && canCancel" />
        
        <!-- Cancel Order -->
        <DropdownMenuItem
          v-if="canCancel"
          :icon="XMarkIcon"
          variant="danger"
          @click="$emit('cancel', order.id)"
        >
          {{ $t('app.views.orders.buttons.cancel') }}
        </DropdownMenuItem>
      </DropdownMenu>
    </div>
    
    <!-- Card Content -->
    <div class="p-4 pr-12">
      <!-- Order Header -->
      <div class="flex items-start justify-between gap-3 mb-3">
        <div class="flex-1 min-w-0">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white truncate">
            #{{ order.order_number || order.id }}
          </h3>
          <div class="mt-1 flex flex-wrap gap-1.5">
            <span 
              class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium"
              :class="statusBadgeClass"
            >
              {{ $t('app.status.' + order.status) }}
            </span>
            <span
              class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium"
              :class="order.is_paid 
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200' 
                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200'"
            >
              {{ order.is_paid ? $t('app.views.orders.payment.paid') : $t('app.views.orders.payment.pending') }}
            </span>
            <span
              v-if="orderType"
              class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200"
            >
              {{ orderType }}
            </span>
          </div>
        </div>
        
        <div class="flex-shrink-0 text-right">
          <p class="text-xl font-bold text-gray-900 dark:text-white">
            ${{ order.total.toFixed(2) }}
          </p>
        </div>
      </div>

      <!-- Order Details -->
      <div class="space-y-2 mb-3">
        <div class="flex items-center text-sm text-gray-500 dark:text-gray-400">
          <RectangleGroupIcon class="flex-shrink-0 mr-2 h-4 w-4 text-gray-400 dark:text-gray-500" />
          <span class="truncate">{{ order.table }}</span>
        </div>
        
        <!-- Show customer name for takeaway/delivery orders -->
        <div 
          v-if="showCustomerName" 
          class="flex items-center text-sm text-gray-500 dark:text-gray-400"
        >
          <UserIcon class="flex-shrink-0 mr-2 h-4 w-4 text-gray-400 dark:text-gray-500" />
          <span class="truncate">{{ order.customer_name || 'Cliente' }}</span>
        </div>
        
        <div class="flex items-center text-sm text-gray-500 dark:text-gray-400">
          <ClockIcon class="flex-shrink-0 mr-2 h-4 w-4 text-gray-400 dark:text-gray-500" />
          <span>{{ formattedTime }}</span>
        </div>
      </div>

      <!-- Order Items Summary -->
      <div class="pt-3 border-t border-gray-100 dark:border-gray-700">
        <div class="text-sm text-gray-600 dark:text-gray-300">
          <span class="font-medium text-gray-900 dark:text-gray-100">
            {{ order.items.length }} {{ order.items.length === 1 ? $t('app.views.orders.summary.item') : $t('app.views.orders.summary.items') }}
          </span>
          <span class="mx-1">•</span>
          <span class="line-clamp-2">{{ itemsSummary }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import DropdownMenu from '@/components/ui/DropdownMenu.vue';
import DropdownMenuItem from '@/components/ui/DropdownMenuItem.vue';
import DropdownMenuDivider from '@/components/ui/DropdownMenuDivider.vue';
import { 
  EyeIcon, 
  PencilIcon, 
  CheckCircleIcon, 
  XMarkIcon,
  RectangleGroupIcon,
  ClockIcon,
  UserIcon
} from '@heroicons/vue/24/outline';
import { getStatusBadgeClass, formatTime, getOrderItemsSummary, getOrderTypeLabel, canCancelOrder } from '@/utils/orderHelpers';
import type { OrderWithLocalFields } from '@/utils/orderHelpers';
import { canCancelOrders } from '@/utils/permissions';
import { useAuthStore } from '@/stores/auth';

const authStore = useAuthStore();

const props = defineProps<{
  order: OrderWithLocalFields;
}>();

defineEmits<{
  view: [order: OrderWithLocalFields];
  edit: [order: OrderWithLocalFields];
  complete: [orderId: number];
  cancel: [orderId: number];
}>();

const statusBadgeClass = computed(() => getStatusBadgeClass(props.order.status));
const formattedTime = computed(() => formatTime(props.order.createdAt));
const itemsSummary = computed(() => getOrderItemsSummary(props.order.items));
const canCancel = computed(() => {
  // Only admin/sysadmin can cancel orders AND order must be cancellable
  return canCancelOrders(authStore.user) && canCancelOrder(props.order);
});
const orderType = computed(() => {
  const type = (props.order as any).order_type;
  return type ? getOrderTypeLabel(type) : null;
});
const showCustomerName = computed(() => {
  const type = (props.order as any).order_type;
  // Show customer name for takeaway and delivery orders
  return type === 'takeaway' || type === 'delivery';
});
</script>
